import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import SEO from '../components/SEO';

const QuoteContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a0033 30%, #330066 60%, #000000 100%);
  padding: 120px 20px 60px;
`;

const QuoteContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #fff 0%, #87ceeb 50%, #fff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255,255,255,0.8);
  margin-bottom: 40px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(135,206,235,0.1);
  border: 1px solid rgba(135,206,235,0.3);
  color: #87ceeb;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 40px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(135,206,235,0.2);
    border-color: rgba(135,206,235,0.5);
  }
`;

const FormCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 25px;
  padding: 50px;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #87ceeb;
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(135,206,235,0.3);
  border-radius: 12px;
  font-size: 16px;
  background: rgba(135,206,235,0.1);
  color: #ffffff;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
  }
  
  &::placeholder {
    color: rgba(255,255,255,0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(135,206,235,0.3);
  border-radius: 12px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  background: rgba(135,206,235,0.1);
  color: #ffffff;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
  }
  
  &::placeholder {
    color: rgba(255,255,255,0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(135,206,235,0.3);
  border-radius: 12px;
  font-size: 16px;
  background: rgba(135,206,235,0.1);
  color: #ffffff;
  transition: border-color 0.3s ease;
  max-height: 200px;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
  }
  
  option {
    background: #1e1b4b;
    color: #ffffff;
    padding: 8px 12px;
  }
  
  optgroup {
    background: #0a0e27;
    color: #87ceeb;
    font-weight: 600;
    padding: 8px 0;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 18px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const Quote = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const service = params.get('service');
    if (service) {
      setFormData(prev => ({ ...prev, service: decodeURIComponent(service) }));
    }
  }, [location]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappMessage = `Hi InitX Technologies!

I'm interested in getting a quote for your services. Here are my details:

*Name:* ${formData.name}
*Email:* ${formData.email}
*Phone:* ${formData.phone}
*Company:* ${formData.company}
*Service Needed:* ${formData.service}
*Budget Range:* ${formData.budget}
*Timeline:* ${formData.timeline}
*Project Details:* ${formData.message}

Please get in touch with me to discuss the quote.

Thanks!`;
    
    const whatsappURL = `https://wa.me/918986283243?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappURL, '_blank');
  };

  const handleBack = () => {
    window.history.back();
  };

  return (
    <QuoteContainer>
      <SEO
        title="Get a Quote | InitX Technologies"
        description="Request a project quote from InitX Technologies for AI development, cloud solutions, web, and mobile applications."
        keywords="project quote, software development estimate, AI services quote, cloud solutions, web development"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Quote', url: '/quote' }
        ]}
      />
      <QuoteContent>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={20} />
          Back to Services
        </BackButton>
        
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Get Your Quote
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Tell us about your project and we'll provide you with a detailed quote
          </Subtitle>
        </Header>

        <FormCard
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <form onSubmit={handleSubmit}>
            <FormGrid>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your phone number"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Company Name</Label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Enter your company name"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Service Required *</Label>
                <Select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a service</option>
                  <option value="Custom Software Development">Custom Software Development</option>
                  <option value="Mobile App Development">Mobile App Development</option>
                  <option value="AI & Machine Learning">AI & Machine Learning</option>
                  <option value="Cloud Solutions">Cloud Solutions</option>
                  <option value="Cybersecurity Services">Cybersecurity Services</option>
                  <option value="Data Analytics & BI">Data Analytics & BI</option>
                  <option value="UI/UX Design">UI/UX Design</option>
                  <option value="Digital Transformation">Digital Transformation</option>
                  <option value="24/7 Support & Maintenance">24/7 Support & Maintenance</option>
                  <option value="E-commerce Development">E-commerce Development</option>
                  <option value="Web Development">Web Development</option>
                  <option value="DevOps & CI/CD">DevOps & CI/CD</option>
                  <option value="Database Design & Management">Database Design & Management</option>
                  <option value="API Development & Integration">API Development & Integration</option>
                  <option value="Quality Assurance & Testing">Quality Assurance & Testing</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Budget Range *</Label>
                <Select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select budget range</option>
                  <optgroup label="Small Projects">
                    <option value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</option>
                    <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                  </optgroup>
                  <optgroup label="Medium Projects">
                    <option value="₹1,00,000 - ₹2,50,000">₹1,00,000 - ₹2,50,000</option>
                    <option value="₹2,50,000 - ₹5,00,000">₹2,50,000 - ₹5,00,000</option>
                  </optgroup>
                  <optgroup label="Large Projects">
                    <option value="₹5,00,000 - ₹10,00,000">₹5,00,000 - ₹10,00,000</option>
                    <option value="₹10,00,000 - ₹25,00,000">₹10,00,000 - ₹25,00,000</option>
                  </optgroup>
                  <optgroup label="Enterprise Projects">
                    <option value="₹25,00,000 - ₹50,00,000">₹25,00,000 - ₹50,00,000</option>
                    <option value="₹50,00,000+">₹50,00,000+</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Custom Quote Required">Custom Quote Required</option>
                    <option value="Discuss Budget">Discuss Budget</option>
                  </optgroup>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Project Timeline</Label>
                <Select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                >
                  <option value="">Select timeline</option>
                  <optgroup label="Urgent">
                    <option value="1-2 weeks">1-2 weeks</option>
                    <option value="3-4 weeks">3-4 weeks</option>
                  </optgroup>
                  <optgroup label="Standard">
                    <option value="1-2 months">1-2 months</option>
                    <option value="3-4 months">3-4 months</option>
                    <option value="5-6 months">5-6 months</option>
                  </optgroup>
                  <optgroup label="Extended">
                    <option value="6-12 months">6-12 months</option>
                    <option value="12+ months">12+ months</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Flexible">Flexible</option>
                    <option value="Discuss Timeline">Discuss Timeline</option>
                  </optgroup>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label>Additional Requirements</Label>
                <Select
                  name="requirements"
                  onChange={handleInputChange}
                >
                  <option value="">Select additional requirements</option>
                  <optgroup label="Web Development">
                    <option value="SEO Optimization">SEO Optimization</option>
                    <option value="Responsive Design">Responsive Design</option>
                    <option value="Content Management System">Content Management System</option>
                    <option value="E-commerce Integration">E-commerce Integration</option>
                  </optgroup>
                  <optgroup label="Mobile Development">
                    <option value="Cross-platform Development">Cross-platform Development</option>
                    <option value="App Store Optimization">App Store Optimization</option>
                    <option value="Push Notifications">Push Notifications</option>
                    <option value="Offline Functionality">Offline Functionality</option>
                  </optgroup>
                  <optgroup label="Integration & APIs">
                    <option value="Payment Gateway Integration">Payment Gateway Integration</option>
                    <option value="Third-party API Integration">Third-party API Integration</option>
                    <option value="Social Media Integration">Social Media Integration</option>
                    <option value="CRM Integration">CRM Integration</option>
                  </optgroup>
                  <optgroup label="Advanced Features">
                    <option value="Multi-language Support">Multi-language Support</option>
                    <option value="Admin Dashboard">Admin Dashboard</option>
                    <option value="Analytics Integration">Analytics Integration</option>
                    <option value="Real-time Features">Real-time Features</option>
                    <option value="AI/ML Integration">AI/ML Integration</option>
                  </optgroup>
                  <optgroup label="Security & Compliance">
                    <option value="Data Encryption">Data Encryption</option>
                    <option value="GDPR Compliance">GDPR Compliance</option>
                    <option value="Security Audit">Security Audit</option>
                    <option value="SSL Certificate">SSL Certificate</option>
                  </optgroup>
                  <optgroup label="Support & Maintenance">
                    <option value="24/7 Support">24/7 Support</option>
                    <option value="Regular Updates">Regular Updates</option>
                    <option value="Performance Monitoring">Performance Monitoring</option>
                    <option value="Backup Solutions">Backup Solutions</option>
                  </optgroup>
                </Select>
              </FormGroup>
              
              <FormGroup className="full-width">
                <Label>Project Details</Label>
                <TextArea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project requirements, goals, target audience, and any specific features you need..."
                />
              </FormGroup>
            </FormGrid>
            
            <SubmitButton type="submit">
              <Send size={20} />
              Send Quote Request
            </SubmitButton>
          </form>
        </FormCard>
      </QuoteContent>
    </QuoteContainer>
  );
};

export default Quote;
